import axios from 'axios';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

// cn function for shadcn components
export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export const API = '/api';

// Create axios instance
export const api = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add auth token to all requests (mobile cookie fallback)
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('session_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Format currency in Indian Rupees (Lakhs/Crores)
export const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return '₹0';
  
  const absAmount = Math.abs(amount);
  const sign = amount < 0 ? '-' : '';
  
  if (absAmount >= 10000000) {
    // Crores
    return `${sign}₹${(absAmount / 10000000).toFixed(2)}Cr`;
  } else if (absAmount >= 100000) {
    // Lakhs
    return `${sign}₹${(absAmount / 100000).toFixed(2)}L`;
  } else if (absAmount >= 1000) {
    // Thousands with comma
    return `${sign}₹${absAmount.toLocaleString('en-IN')}`;
  }
  
  return `${sign}₹${absAmount.toLocaleString('en-IN')}`;
};

// Format full currency without abbreviation
export const formatFullCurrency = (amount) => {
  if (amount === null || amount === undefined) return '₹0';
  return `₹${Math.abs(amount).toLocaleString('en-IN')}`;
};

// Format date
export const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

// Format relative date
export const formatRelativeDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  
  return formatDate(dateString);
};

// Get today's date in YYYY-MM-DD format
export const getTodayDate = () => {
  return new Date().toISOString().split('T')[0];
};

// Get current month start date
export const getMonthStartDate = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
};

// Expense categories
export const expenseCategories = [
  { value: 'ingredients', label: 'Ingredients', group: 'Operations' },
  { value: 'packaging', label: 'Packaging', group: 'Operations' },
  { value: 'kitchen_supplies', label: 'Kitchen Supplies', group: 'Operations' },
  { value: 'salaries', label: 'Salaries', group: 'People' },
  { value: 'freelancers', label: 'Freelancers', group: 'People' },
  { value: 'rent', label: 'Rent', group: 'Fixed' },
  { value: 'utilities', label: 'Utilities', group: 'Fixed' },
  { value: 'marketing', label: 'Marketing', group: 'Growth' },
  { value: 'influencers', label: 'Influencers/Content', group: 'Growth' },
  { value: 'equipment', label: 'Equipment', group: 'Business' },
  { value: 'repairs', label: 'Repairs/Maintenance', group: 'Business' },
  { value: 'software', label: 'Software/Subscriptions', group: 'Business' },
  { value: 'transportation', label: 'Transportation/Delivery', group: 'Logistics' },
  { value: 'owner_withdrawal', label: 'Owner Withdrawal', group: 'Owner' },
  { value: 'credit_card_settlement', label: 'Credit Card Settlement', group: 'Finance' },
  { value: 'inter_account_transfer', label: 'Inter-Account Transfer', group: 'Finance' },
  { value: 'other', label: 'Other', group: 'Other' }
];

// Tax categories
export const taxCategories = [
  { value: 'cogs', label: 'Cost of Goods Sold' },
  { value: 'employee_expense', label: 'Employee Expense' },
  { value: 'rent_expense', label: 'Rent Expense' },
  { value: 'utility_expense', label: 'Utility Expense' },
  { value: 'marketing_expense', label: 'Marketing Expense' },
  { value: 'office_expense', label: 'Office Expense' },
  { value: 'travel_expense', label: 'Travel Expense' },
  { value: 'professional_fees', label: 'Professional Fees' },
  { value: 'depreciation', label: 'Depreciation' },
  { value: 'interest_expense', label: 'Interest Expense' },
  { value: 'other_expense', label: 'Other Expense' },
  { value: 'drawings', label: 'Drawings' }
];

// Payment modes
export const paymentModes = [
  { value: 'upi', label: 'UPI' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'cash', label: 'Cash' },
  { value: 'card', label: 'Card' },
  { value: 'online', label: 'Online Payment' },
  { value: 'subscription', label: 'Subscription' }
];

// Investment types
export const investmentTypes = [
  { value: 'mutual_funds', label: 'Mutual Funds' },
  { value: 'stocks', label: 'Stocks' },
  { value: 'gold', label: 'Gold' },
  { value: 'fixed_deposit', label: 'Fixed Deposit' },
  { value: 'recurring_deposit', label: 'Recurring Deposit' },
  { value: 'crypto', label: 'Crypto' },
  { value: 'real_estate', label: 'Real Estate' }
];

// CapEx categories — grouped by phase for easier picking
export const capexCategories = [
  // Design & Planning
  { value: 'architect_design', label: 'Architect/Design', group: 'Design & Planning' },
  { value: 'consultancy', label: 'Consultancy/Professional Fees', group: 'Design & Planning' },
  { value: 'legal', label: 'Legal/Documentation', group: 'Design & Planning' },
  // Build-out
  { value: 'construction', label: 'Construction/Civil Work', group: 'Build-out' },
  { value: 'interior', label: 'Interior/Flooring/Lighting', group: 'Build-out' },
  { value: 'plumbing', label: 'Plumbing', group: 'Build-out' },
  { value: 'electrical', label: 'Electrical Wiring', group: 'Build-out' },
  { value: 'hvac', label: 'HVAC/Air Conditioning', group: 'Build-out' },
  // Equipment & Assets
  { value: 'kitchen_equipment', label: 'Kitchen Equipment', group: 'Equipment & Assets' },
  { value: 'furniture', label: 'Furniture/Decor', group: 'Equipment & Assets' },
  { value: 'smallwares', label: 'Smallwares/Crockery/Cutlery', group: 'Equipment & Assets' },
  { value: 'technology', label: 'Technology/POS', group: 'Equipment & Assets' },
  // Compliance
  { value: 'licenses', label: 'Licenses/Permits', group: 'Compliance' },
  { value: 'deposits', label: 'Security/Utility Deposits', group: 'Compliance' },
  { value: 'insurance', label: 'Insurance', group: 'Compliance' },
  // People
  { value: 'uniforms', label: 'Uniforms', group: 'People' },
  { value: 'staff_training', label: 'Staff Training', group: 'People' },
  // Launch
  { value: 'branding', label: 'Branding/Signage', group: 'Launch' },
  { value: 'launch_marketing', label: 'Launch Marketing', group: 'Launch' },
  { value: 'initial_inventory', label: 'Initial Inventory/Opening Stock', group: 'Launch' },
  { value: 'pre_opening', label: 'Pre-Opening Costs', group: 'Launch' },
  { value: 'transportation', label: 'Transportation/Logistics', group: 'Launch' },
  // Other
  { value: 'miscellaneous', label: 'Miscellaneous', group: 'Other' },
  { value: 'other', label: 'Other', group: 'Other' }
];

// Get category label
export const getCategoryLabel = (value, categories = expenseCategories) => {
  const cat = categories.find(c => c.value === value);
  return cat ? cat.label : value;
};

// Get category color
export const getCategoryColor = (category) => {
  const colors = {
    ingredients: '#18181B',
    packaging: '#27272A',
    kitchen_supplies: '#3F3F46',
    salaries: '#52525B',
    freelancers: '#71717A',
    rent: '#A1A1AA',
    utilities: '#D4D4D8',
    marketing: '#22C55E',
    influencers: '#16A34A',
    equipment: '#3B82F6',
    repairs: '#2563EB',
    software: '#1D4ED8',
    transportation: '#F59E0B',
    owner_withdrawal: '#EF4444',
    credit_card_settlement: '#DC2626',
    other: '#9CA3AF'
  };
  return colors[category] || '#71717A';
};
